// Global
import React from 'react';

// Local
import { pushCtaContentResultEvent } from 'components/helpers/Analytics/Cta/CtaAnalytics';
import styles from './VerticalVideoCard.module.scss';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import useDictionary from 'lib/hooks/useDictionary';
import useEmbeddedVideo from 'lib/hooks/video/useEmbeddedVideo';

export interface VerticalVideoCardData {
  videoId: string;
  videoTitle: string;
  videoDescription?: string;
  videoThumbnailUrl: string;
  videoType: string;
  videoUrl: string;
  videoWithAudioDescriptionURL?: string;
  videoTranscriptFileUrl?: string;
}

export interface VerticalVideoCardProps {
  contentListName?: string;
  video: VerticalVideoCardData;
  onPlay: (video: VerticalVideoCardData, trigger?: HTMLButtonElement) => void;
  ctaLinksLeftAligned?: boolean;
}

export default function VerticalVideoCard({
  contentListName,
  video,
  onPlay,
  ctaLinksLeftAligned = true,
}: VerticalVideoCardProps) {
  const { getDictionaryValue } = useDictionary();

  const authoredVideoUrl = (video.videoUrl || '').trim();
  const authoredVideoId = (video.videoId || '').trim();
  const authoredVideoType = (video.videoType || '').trim();

  const oEmbedVideoUrl = authoredVideoUrl
    ? authoredVideoUrl
    : (() => {
        if (!authoredVideoId) return '';

        switch (authoredVideoType) {
          case 'YouTube':
            return `https://www.youtube.com/watch?v=${authoredVideoId}`;
          case 'YouTube Shorts':
            return `https://www.youtube.com/shorts/${authoredVideoId}`;
          case 'TikTok':
            return `https://www.tiktok.com/embed/${authoredVideoId}`;
          case 'Instagram':
            return `https://www.instagram.com/reel/${authoredVideoId}/`;
          default:
            return '';
        }
      })();

  const { extractedVideoThumbnail, extractedVideoType } = useEmbeddedVideo(oEmbedVideoUrl);

  const authoredThumbnail = video.videoThumbnailUrl?.trim();
  const thumbnailUrl = authoredThumbnail || extractedVideoThumbnail || '';
  const hasThumbnail = Boolean(thumbnailUrl);
  const platformLabel = (video.videoType || extractedVideoType || 'Video').trim() || 'Video';
  const hasTitle = Boolean(video.videoTitle?.trim());
  const hasDescription = Boolean(video.videoDescription?.trim());
  const isCopyEmpty = !hasTitle && !hasDescription;

  const trackVideoSelection = () => {
    pushCtaContentResultEvent({
      contentListName,
      linkUrl: oEmbedVideoUrl || undefined,
    });
  };

  const handlePlayClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackVideoSelection();
    onPlay(video, event.currentTarget);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      trackVideoSelection();
      onPlay(video, event.currentTarget);
    }
  };

  return (
    <article className={styles.card} aria-label={`${platformLabel}: ${video.videoTitle}`}>
      <div className={styles.cardMediaContainer}>
        {/* Thumbnail with Play Button Overlay */}
        <div
          className={`${styles.cardThumbnailContainer} ${!hasThumbnail ? styles.cardThumbnailContainerFallback : ''}`}
        >
          <button
            className={styles.cardPlayButton}
            onClick={handlePlayClick}
            onKeyDown={handleKeyDown}
            aria-label={`${getDictionaryValue('Helpers.EmbeddedVideo.PlayButton', 'Play')} ${platformLabel} ${getDictionaryValue('Helpers.VerticalVideo.Video', 'video')}: ${video.videoTitle}`}
          >
            {hasThumbnail && (
              <EnhancedNextImage
                src={thumbnailUrl}
                alt={`${getDictionaryValue('Helpers.EmbeddedVideo.ThumbnailAlt', 'Thumbnail')} ${video.videoTitle}`}
                width={337}
                height={882}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                className={styles.cardThumbnailImage}
              />
            )}
            <span className={styles.cardPlayIcon} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className={styles.cardCopyContainer}>
        {/* Card Content: Title + Description */}
        <div className={`${styles.cardContent} ${isCopyEmpty ? styles.cardContentEmpty : ''}`}>
          {hasTitle ? (
            <h3 className={styles.cardTitle}>{video.videoTitle}</h3>
          ) : (
            <span className={styles.cardTitlePlaceholder} aria-hidden="true" />
          )}
          {hasDescription && <p className={styles.cardDescription}>{video.videoDescription}</p>}
        </div>

        {/* Accessibility Links */}
        {(video.videoWithAudioDescriptionURL || video.videoTranscriptFileUrl) && (
          <ul
            className={`${styles.cardLinks} ${ctaLinksLeftAligned ? styles.cardLinksLeftAligned : ''}`}
            aria-label={getDictionaryValue(
              'Helpers.EmbeddedVideo.AccessibilityOptions',
              'Video Accessibility Options'
            )}
          >
            {video.videoWithAudioDescriptionURL && (
              <li className={styles.cardLinksItem}>
                <CtaLink
                  url={video.videoWithAudioDescriptionURL}
                  target="_blank"
                  ctaVariant="brand"
                  ctaStyle="link"
                  text={getDictionaryValue(
                    'Helpers.EmbeddedVideo.VideoWithAudioDescription',
                    'Video with Audio Description'
                  )}
                />
              </li>
            )}
            {video.videoTranscriptFileUrl && (
              <li className={styles.cardLinksItem}>
                <CtaLink
                  url={video.videoTranscriptFileUrl}
                  target="_blank"
                  ctaVariant="brand"
                  ctaStyle="link"
                  text={getDictionaryValue(
                    'Helpers.EmbeddedVideo.TextTranscript',
                    'Text transcript (TXT)'
                  )}
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </article>
  );
}
